import React, { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { defaultFormProps } from 'util/Form';
import {
  AttributeFileAnswer,
  AttributeUploadedFile,
  PreferenceQuestionAnswer,
  PreferenceQuestionView,
  SublistAnswer,
} from 'store/types/AuraAttribute';
import EditPreferencesForm from 'components/profile/EditPreferencesPage/EditPreferencesForm';
import FormFooter from 'components/shared/FormFooter';
import PreferencesService from 'services/api/PreferencesService';
import { useSnackbar } from 'notistack';
import Spinner from 'components/shared/Spinner';
import { getPreferencesAnswersData, getDefaultPreferencesFormValues, getQuestionId } from 'util/Preferences';
import { useLocation } from 'react-router';
import routes from 'store/configs/Routes';
import EventService from 'services/api/EventService';
import { defaultSnackbarErrorProps } from 'util/Layout';

interface EditCustomPreferencesPageViewProps {
  data: PreferenceQuestionView[];
  alternateId?: string;
}

const isBlobFile = (x: unknown): x is File => typeof File !== 'undefined' && x instanceof File;

const EditPreferencesPageView: React.FunctionComponent<EditCustomPreferencesPageViewProps> = ({
  data,
  alternateId,
}) => {
  const defaultValues = useMemo(() => getDefaultPreferencesFormValues(data), [data]);
  const form = useForm({ ...defaultFormProps, defaultValues });
  const { pathname } = useLocation();
  const { handleSubmit } = form;
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const uploadFiles = useCallback(
    (
      request,
      answersData: PreferenceQuestionAnswer[],
      files: AttributeFileAnswer,
      sublistFiles?: AttributeFileAnswer
    ) => {
      const result: PreferenceQuestionAnswer[] = answersData;
      const uploadPromiseArray: Promise<AttributeUploadedFile>[] = [];

      if (files) {
        Object.entries(files).forEach(([questionId, files]) => {
          if (files?.length) {
            files.forEach((file: File) => {
              if (isBlobFile(file)) {
                uploadPromiseArray.push(PreferencesService.uploadFile(file, questionId));
              }
            });
          }
        });
      }

      if (sublistFiles) {
        Object.entries(sublistFiles).forEach(([referenceKey, sublistAnswer]) => {
          const questionId = sublistAnswer?.questionId;
          const sublistQuestionId = sublistAnswer?.sublistQuestionId;
          const files = sublistAnswer?.files;

          if (files?.length) {
            files.forEach((file: File) => {
              if (isBlobFile(file)) {
                uploadPromiseArray.push(
                  PreferencesService.uploadFile(file, questionId, sublistQuestionId, referenceKey)
                );
              }
            });
          }
        });
      }

      if (!uploadPromiseArray.length) {
        request(answersData);
        return;
      }

      Promise.all(uploadPromiseArray)
        .then((data: AttributeUploadedFile[]) => {
          data.forEach((item: AttributeUploadedFile) => {
            const fileQuestionId: string = getQuestionId(item.questionId);
            const answerIndex = result.findIndex(({ attributeId }) => attributeId === fileQuestionId);

            console.log('file upload result', { item, fileQuestionId, answerIndex });

            if (answerIndex >= 0 && item.sublistQuestionId && item.referenceKey) {
              const sublistAnswerSet: any = result[answerIndex]?.sublistAnswers || [];
              let setIndex = -1;
              let sublistAnswerIndex = -1;
              sublistAnswerSet.forEach((answerSet: any, outerIndex: number) =>
                answerSet.forEach(({ sublistQuestionId, referenceKey }: SublistAnswer, innerIndex: number) => {
                  if (sublistQuestionId === item.sublistQuestionId && referenceKey === item.referenceKey) {
                    setIndex = outerIndex;
                    sublistAnswerIndex = innerIndex;
                  }
                })
              );

              if (setIndex >= 0 && sublistAnswerIndex >= 0) {
                const existing = sublistAnswerSet[setIndex][sublistAnswerIndex] || [];
                if (existing) existing.files = [...(existing.files || []), { id: item.fileId }];
              }
            } else if (answerIndex >= 0) {
              result[answerIndex].answer = 'File';
              result[answerIndex].files = [...(result[answerIndex].files || []), { id: item.fileId }];
            }
          });

          request(result);
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [enqueueSnackbar]
  );

  const handleFormSubmit = useCallback(
    (formValues) => {
      setSubmitLoading(true);
      const answersData: PreferenceQuestionAnswer[] = getPreferencesAnswersData(formValues, data);
      const files: AttributeFileAnswer = formValues['files'];
      const sublistFiles: AttributeFileAnswer = formValues['sublistFiles'];

      uploadFiles(
        (data: PreferenceQuestionAnswer[]) =>
          (alternateId
            ? pathname.includes(routes.viewPrivatePreferences)
              ? PreferencesService.updatePreferenceQuestionsByAlternateId(alternateId, data)
              : EventService.updatePreferenceQuestionsByAlternateId(alternateId, data)
            : PreferencesService.updatePreferenceQuestions(data)
          )
            .then(() => {
              setSubmitLoading(false);
              enqueueSnackbar('Preferences successfully updated', { variant: 'success' });
            })
            .catch((error: string) => {
              setSubmitLoading(false);
              enqueueSnackbar(error, defaultSnackbarErrorProps);
            }),
        answersData,
        files,
        sublistFiles
      );
    },
    [alternateId, data, enqueueSnackbar, pathname, uploadFiles]
  );

  const handleSubmitButtonClick = useCallback(() => {
    window.scrollTo(0, 0);
    handleSubmit(handleFormSubmit)();
  }, [handleFormSubmit, handleSubmit]);

  return (
    <Spinner loading={submitLoading}>
      <FormProvider {...form}>
        <EditPreferencesForm data={data} />
        <FormFooter onSubmit={handleSubmitButtonClick} />
      </FormProvider>
    </Spinner>
  );
};

export default EditPreferencesPageView;
